import { useEffect, useState } from 'react';
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Home,
  GraduationCap,
  Calendar,
  UserCircle2,
  Activity,
  Menu,
  X,
  LogOut,
} from 'lucide-react';
import { isLocale, type Locale } from '@/client/i18n';
import { useMe } from '@/client/hooks/useMe';
import { Alert } from '@/client/components/ui/alert';
import { api } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Seo } from '@/client/components/Seo';
import { cn } from '@/client/lib/utils';

const NAV_ITEMS = [
  { key: 'nav_dashboard', path: '', Icon: Home },
  { key: 'nav_courses', path: 'kurzy', Icon: GraduationCap },
  { key: 'nav_bookings', path: 'rezervacie', Icon: Calendar },
  { key: 'nav_profile', path: 'profil', Icon: UserCircle2 },
  { key: 'nav_sessions', path: 'profil/relacie', Icon: Activity },
] as const;

export function PortalLayout() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const { data: me, isLoading } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && me === null) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/${locale}/prihlasenie?redirectTo=${redirect}`, { replace: true });
    }
  }, [isLoading, me, navigate, location, locale]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }
  if (!me) return <Navigate to={`/${locale}/prihlasenie`} replace />;

  async function handleLogout() {
    await api('/api/v1/auth/session', { method: 'DELETE' });
    queryClient.setQueryData(['me'], null);
    navigate(`/${locale}/`);
  }

  async function handleResendVerification() {
    if (!me) return;
    await api('/api/v1/auth/email/verify/resend', {
      method: 'POST',
      body: { email: me.email },
    });
  }

  async function handleRestore() {
    const data = await api<{ user: typeof me }>('/api/v1/me/restore', { method: 'POST' });
    queryClient.setQueryData(['me'], data.user);
  }

  const prefix = `/${locale}/portal`;
  const firstName = me.name?.split(' ')[0] ?? me.name ?? '';

  return (
    <div className="relative min-h-screen">
      {/* Ambient background */}
      <div
        className="fixed inset-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 0% 0%, rgba(193,167,226,0.30), transparent 65%),' +
            'radial-gradient(ellipse 50% 40% at 100% 70%, rgba(117,59,189,0.10), transparent 65%),' +
            'linear-gradient(180deg, #faf8fc 0%, #f3eff7 100%)',
        }}
        aria-hidden
      />
      <div className="grain-overlay" aria-hidden />

      <Seo title={t('nav.portal')} locale={locale} index={false} />

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-glass-border-strong px-4 h-14 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="inline-flex h-10 w-10 items-center justify-center rounded-pill bg-white/70 border border-glass-border-strong text-primary"
        >
          <Menu size={18} />
        </button>
        <p className="font-display text-h4 text-primary-dark">{t('portal.greeting', { name: firstName })}</p>
        <div className="w-10" />
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-6 lg:py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
          {/* Sidebar — desktop */}
          <aside className="hidden lg:block">
            <div className="sticky top-6">
              <div className="dr-card-static p-2">
                <div className="px-3 py-4 mb-2 border-b border-glass-border">
                  <p className="font-sans text-caption uppercase tracking-[0.18em] text-primary">
                    {t('portal.greeting_eyebrow', { defaultValue: 'Vitaj' })}
                  </p>
                  <p className="mt-1 font-display text-h4 text-primary-dark truncate">
                    {firstName}
                  </p>
                </div>
                <nav className="space-y-0.5 p-1">
                  {NAV_ITEMS.map((item) => {
                    const target = item.path ? `${prefix}/${item.path}` : prefix;
                    const Icon = item.Icon;
                    return (
                      <NavLink
                        key={item.key}
                        to={target}
                        end={item.path === ''}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 px-3 py-2.5 rounded-md font-sans text-body-small transition-all',
                            isActive
                              ? 'bg-gradient-to-r from-primary/15 to-primary/5 text-primary-dark font-medium shadow-glass-soft'
                              : 'text-text-primary hover:bg-primary-50/60',
                          )
                        }
                      >
                        <Icon size={16} className="shrink-0 opacity-80" />
                        <span>{t(`portal.${item.key}`)}</span>
                      </NavLink>
                    );
                  })}
                </nav>
                <div className="mt-2 pt-2 border-t border-glass-border p-1">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md font-sans text-body-small text-text-secondary hover:bg-primary-50/60 hover:text-primary transition-colors"
                  >
                    <LogOut size={16} className="opacity-80" />
                    {t('portal.logout')}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile drawer */}
          <div
            className={cn(
              'fixed inset-0 z-40 lg:hidden transition-opacity duration-300',
              mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
            )}
            aria-hidden={!mobileOpen}
          >
            <button
              type="button"
              tabIndex={-1}
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="absolute inset-0 bg-primary-900/40 backdrop-blur-sm"
            />
            <aside
              className={cn(
                'absolute left-0 top-0 h-full w-[82%] max-w-xs bg-white/95 backdrop-blur-2xl border-r border-glass-border-strong shadow-[8px_0_60px_rgba(81,45,109,0.25)] transition-transform duration-300',
                mobileOpen ? 'translate-x-0' : '-translate-x-full',
              )}
            >
              <div className="p-5 flex items-center justify-between border-b border-glass-border">
                <p className="font-display text-h4 text-primary-dark truncate">{firstName}</p>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-pill text-primary hover:bg-primary-50"
                >
                  <X size={18} />
                </button>
              </div>
              <nav className="p-3 space-y-1">
                {NAV_ITEMS.map((item) => {
                  const target = item.path ? `${prefix}/${item.path}` : prefix;
                  const Icon = item.Icon;
                  return (
                    <NavLink
                      key={item.key}
                      to={target}
                      end={item.path === ''}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-4 py-3 rounded-md font-sans text-body transition-colors',
                          isActive
                            ? 'bg-primary-50 text-primary font-medium'
                            : 'text-text-primary hover:bg-primary-50/60',
                        )
                      }
                    >
                      <Icon size={18} />
                      {t(`portal.${item.key}`)}
                    </NavLink>
                  );
                })}
              </nav>
              <div className="absolute bottom-0 inset-x-0 p-4 border-t border-glass-border bg-white/80 backdrop-blur-md">
                <Button variant="ghost" size="md" className="w-full" onClick={handleLogout}>
                  <LogOut size={16} /> {t('portal.logout')}
                </Button>
              </div>
            </aside>
          </div>

          {/* Main */}
          <main className="min-h-[60vh]">
            <div className="hidden lg:flex items-center justify-between mb-8">
              <div>
                <p className="font-sans text-caption uppercase tracking-[0.18em] text-primary">
                  {t('portal.greeting_eyebrow', { defaultValue: 'Tvoj priestor' })}
                </p>
                <h1 className="mt-1 font-display text-h1 text-primary-dark">
                  {t('portal.greeting', { name: firstName })}
                </h1>
              </div>
            </div>

            {!me.emailVerified && (
              <Alert
                variant="warning"
                className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <span>{t('portal.verify_banner')}</span>
                <Button size="sm" variant="secondary" onClick={handleResendVerification}>
                  {t('portal.verify_banner_action')}
                </Button>
              </Alert>
            )}

            {me.deletedAt !== null && me.hardDeleteAt !== null && (
              <Alert variant="error" className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-sans text-body font-semibold">
                    {t('portal.deletion_banner_title')}
                  </p>
                  <p className="font-sans text-body-small">
                    {t('portal.deletion_banner_body', {
                      date: new Date(me.hardDeleteAt * 1000).toLocaleDateString(
                        locale === 'cs' ? 'cs-CZ' : 'sk-SK',
                      ),
                    })}
                  </p>
                </div>
                <Button size="sm" variant="secondary" onClick={handleRestore}>
                  {t('portal.deletion_banner_action')}
                </Button>
              </Alert>
            )}

            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
